import React, { useState, useEffect } from 'react';
import { FaCog, FaBell, FaSave, FaCalendarAlt, FaClock } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Component for mentors to configure account settings and preferences
 */
const MentorSettings = () => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      pushNotifications: true,
      sessionReminders: true,
      marketingEmails: false
    },
    availability: {
      availableDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false
      },
      availableHours: {
        start: '09:00',
        end: '17:00'
      },
      sessionDuration: 60,
      breakBetweenSessions: 15
    },
    preferences: {
      language: 'en',
      timezone: 'Africa/Nairobi',
      currency: 'KES'
    }
  });
  
  // Fetch settings on component mount
  useEffect(() => {
    fetchSettings();
  }, []);
  
  // Fetch settings from API
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/mentors/settings');
      
      if (response.data.success && response.data.data) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      showError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle notification settings changes
  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    
    setSettings({
      ...settings,
      notifications: {
        ...settings.notifications,
        [name]: checked
      }
    });
  };
  
  // Handle availability day changes
  const handleDayChange = (e) => {
    const { name, checked } = e.target;
    
    setSettings({
      ...settings,
      availability: {
        ...settings.availability,
        availableDays: {
          ...settings.availability.availableDays,
          [name]: checked
        }
      }
    });
  };
  
  // Handle availability hours changes
  const handleHoursChange = (e) => {
    const { name, value } = e.target;
    
    setSettings({
      ...settings,
      availability: {
        ...settings.availability,
        availableHours: {
          ...settings.availability.availableHours,
          [name]: value
        }
      }
    });
  };
  
  // Handle session duration/break change
  const handleDurationChange = (e) => {
    const { name, value } = e.target;
    
    setSettings({
      ...settings,
      availability: {
        ...settings.availability,
        [name]: parseInt(value, 10)
      }
    });
  };
  
  // Handle preference changes
  const handlePreferenceChange = (e) => {
    const { name, value } = e.target;
    
    setSettings({
      ...settings,
      preferences: {
        ...settings.preferences,
        [name]: value
      }
    });
  };
  
  // Save settings
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const response = await api.put('/mentors/settings', settings);
      
      if (response.data.success) {
        showSuccess('Settings saved successfully');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  // Format day name for display
  const formatDayName = (day) => {
    return day.charAt(0).toUpperCase() + day.slice(1);
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6 flex items-center">
        <FaCog className="mr-2 text-primary" /> Account Settings
      </h1>
      
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-6 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Notification Settings */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaBell className="mr-2 text-primary" /> Notification Preferences
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Email Notifications</h3>
                  <p className="text-sm text-gray-500">
                    Receive notifications via email
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="email"
                    className="sr-only peer"
                    checked={settings.notifications.email}
                    onChange={handleNotificationChange}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Push Notifications</h3>
                  <p className="text-sm text-gray-500">
                    Receive push notifications in the browser
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="pushNotifications"
                    className="sr-only peer"
                    checked={settings.notifications.pushNotifications}
                    onChange={handleNotificationChange}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Session Reminders</h3>
                  <p className="text-sm text-gray-500">
                    Get reminded about upcoming mentoring sessions
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="sessionReminders"
                    className="sr-only peer"
                    checked={settings.notifications.sessionReminders}
                    onChange={handleNotificationChange}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Marketing Emails</h3>
                  <p className="text-sm text-gray-500">
                    Receive updates about new features and promotions
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="marketingEmails"
                    className="sr-only peer"
                    checked={settings.notifications.marketingEmails}
                    onChange={handleNotificationChange}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          </div>
          
          {/* Availability Settings */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaCalendarAlt className="mr-2 text-primary" /> Availability Settings
            </h2>
            
            <div className="mb-6">
              <h3 className="font-medium mb-3">Available Days</h3>
              <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
                {Object.keys(settings.availability.availableDays).map((day) => (
                  <div key={day} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`day-${day}`}
                      name={day}
                      checked={settings.availability.availableDays[day]}
                      onChange={handleDayChange}
                      className="w-4 h-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor={`day-${day}`} className="ml-2 text-sm font-medium text-gray-700">
                      {formatDayName(day)}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="font-medium mb-3">Available Hours</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="start" className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    id="start"
                    name="start"
                    value={settings.availability.availableHours.start}
                    onChange={handleHoursChange}
                    className="form-input w-full"
                  />
                </div>
                <div>
                  <label htmlFor="end" className="block text-sm font-medium text-gray-700 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    id="end"
                    name="end"
                    value={settings.availability.availableHours.end}
                    onChange={handleHoursChange}
                    className="form-input w-full"
                  />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="sessionDuration" className="block text-sm font-medium text-gray-700 mb-1">
                  Default Session Duration (minutes)
                </label>
                <select
                  id="sessionDuration"
                  name="sessionDuration"
                  value={settings.availability.sessionDuration}
                  onChange={handleDurationChange}
                  className="form-select w-full"
                >
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">60 minutes</option>
                  <option value="90">90 minutes</option>
                  <option value="120">2 hours</option>
                </select>
              </div>
              <div>
                <label htmlFor="breakBetweenSessions" className="block text-sm font-medium text-gray-700 mb-1">
                  Break Between Sessions (minutes)
                </label>
                <select
                  id="breakBetweenSessions"
                  name="breakBetweenSessions"
                  value={settings.availability.breakBetweenSessions}
                  onChange={handleDurationChange}
                  className="form-select w-full"
                >
                  <option value="0">No break</option>
                  <option value="5">5 minutes</option>
                  <option value="10">10 minutes</option>
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Account Preferences */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaClock className="mr-2 text-primary" /> Account Preferences
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
                  Language
                </label>
                <select
                  id="language"
                  name="language"
                  value={settings.preferences.language}
                  onChange={handlePreferenceChange}
                  className="form-select w-full"
                >
                  <option value="en">English</option>
                  <option value="sw">Swahili</option>
                  <option value="fr">French</option>
                </select>
              </div>
              <div>
                <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone
                </label>
                <select
                  id="timezone"
                  name="timezone"
                  value={settings.preferences.timezone}
                  onChange={handlePreferenceChange}
                  className="form-select w-full"
                >
                  <option value="Africa/Nairobi">East Africa Time (EAT)</option>
                  <option value="Africa/Lagos">West Africa Time (WAT)</option>
                  <option value="Africa/Cairo">Eastern European Time (EET)</option>
                  <option value="UTC">Coordinated Universal Time (UTC)</option>
                </select>
              </div>
              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
                  Currency
                </label>
                <select
                  id="currency"
                  name="currency"
                  value={settings.preferences.currency}
                  onChange={handlePreferenceChange}
                  className="form-select w-full"
                >
                  <option value="KES">Kenyan Shilling (KES)</option>
                  <option value="USD">US Dollar (USD)</option>
                  <option value="GBP">British Pound (GBP)</option>
                  <option value="EUR">Euro (EUR)</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSaveSettings}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              {saving ? <LoadingSpinner size="sm" className="mr-2" /> : <FaSave className="mr-2" />}
              Save Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MentorSettings; 